<template>
  <div class="tax-section">
    <h6 class="section-label mt-0">
      <feather-icon icon="HomeIcon" size="13" class="mr-50" />Title Deed &amp; Area
    </h6>

    <upload-title-deed ref="titleDeedComponent" :id="propertyId" />

    <b-row class="mt-2">
      <b-col cols="12">
        <b-form-group>
          <label>Project Name (Thai)</label>
          <div class="position-relative">
            <b-form-input
              :value="projectName"
              @input="onProjectNameInput"
              @focus="onProjectNameFocus"
              @blur="showSuggestions = false"
              autocomplete="off"
              placeholder="e.g. ดิ อินฟินิตี้"
            />
            <div v-if="showSuggestions && (suggestLoading || projectSuggestions.length)" class="suggest-dropdown">
              <div v-if="suggestLoading" class="suggest-item text-muted small">Searching…</div>
              <template v-else>
                <div
                  v-for="c in projectSuggestions"
                  :key="c.condoId"
                  class="suggest-item"
                  @mousedown.prevent="selectSuggestion(c)"
                >
                  <div class="small font-weight-bold">{{ c.name }}</div>
                  <div class="text-muted" style="font-size: 11px;">{{ c.amphur }}, {{ c.tumbon }}</div>
                </div>
              </template>
            </div>
          </div>
          <small class="text-muted d-block mt-25">
            Treasury Dept. records are Thai-only — edit if the pre-filled name doesn't match.
          </small>
        </b-form-group>
      </b-col>

      <b-col md="6">
        <b-form-group>
          <label>
            Tower / Building (Thai)
            <span class="text-muted font-weight-normal">(optional)</span>
          </label>
          <b-form-input :value="tower" @input="onTowerOrFloorInput('tower', $event)" placeholder="leave blank if one tower" />
        </b-form-group>
      </b-col>

      <b-col md="6">
        <b-form-group>
          <label>Floor (Title Deed)</label>
          <b-form-input :value="floor" @input="onTowerOrFloorInput('floor', $event)" placeholder="e.g. 22" />
        </b-form-group>
      </b-col>

      <b-col md="6">
        <b-form-group>
          <label>
            Unit No. <span class="text-muted font-weight-normal">(optional)</span>
          </label>
          <b-form-input v-model="unitNoModel" placeholder="e.g. 114/36 (164)" />
        </b-form-group>
      </b-col>

      <b-col md="6">
        <b-form-group>
          <label>Unit Area (sqm)</label>
          <b-form-input
            :value="formatNumber(unitArea)"
            @input="v => $emit('update:unitArea', parseNumber(v))"
            type="text"
            placeholder="e.g. 45.5"
          />
        </b-form-group>
      </b-col>

      <b-col md="6">
        <b-form-group>
          <div class="d-flex justify-content-between align-items-baseline mb-50">
            <label class="mb-0">Appraised Rate / sqm</label>

            <b-link
              v-if="lookupStatus === 'idle' || lookupStatus === 'empty'"
              class="small"
              @click="lookupAppraisedRate()"
            >
              <feather-icon icon="SearchIcon" size="12" class="mr-25" />Look up (Treasury Dept.)
            </b-link>
            <span v-else-if="lookupStatus === 'loading'" class="text-muted small">
              <b-spinner small class="mr-25" />Searching…
            </span>
            <span
              v-else-if="lookupStatus === 'matched' || lookupStatus === 'estimated'"
              class="text-success small font-weight-bold"
            >
              <feather-icon icon="CheckIcon" size="12" class="mr-25" />Matched
            </span>
            <span v-else class="small">2+ found</span>
          </div>

          <b-input-group prepend="฿">
            <b-form-input
              :value="formatNumber(appraisedRatePerSqm)"
              @input="v => $emit('update:appraisedRatePerSqm', parseNumber(v))"
              type="text"
              placeholder="e.g. 120,000"
            />
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <b-alert
      v-if="lookupStatus === 'matched' || lookupStatus === 'estimated'"
      :variant="lookupStatus === 'matched' ? 'info' : 'warning'"
      show
      class="p-1 mb-1"
    >
      <div class="d-flex justify-content-between align-items-center mb-50">
        <strong class="small">Treasury Dept. (กรมธนารักษ์)</strong>
        <b-badge :variant="lookupStatus === 'matched' ? 'info' : 'warning'">
          {{ lookupStatus === 'matched' ? 'Exact match' : 'Estimated' }}
        </b-badge>
      </div>
      <div class="small">
        <div>
          <span class="text-muted">Project:</span> {{ matchDetail.condoName }}
          <template v-if="matchDetail.buildName">— {{ matchDetail.buildName }}</template>
        </div>
        <div><span class="text-muted">Floor:</span> {{ matchDetail.floor }}</div>
        <div v-if="matchDetail.useCategory">
          <span class="text-muted">Category:</span> {{ matchDetail.useCategory }}
        </div>
        <div><span class="text-muted">Rate:</span> ฿ {{ formatNumber(matchDetail.rate) }} / sqm</div>
      </div>
      <p v-if="lookupStatus === 'estimated'" class="small text-muted mb-0 mt-50">
        No appraisal row exists for this exact floor. This is the average of
        {{ matchDetail.sampleSize }} residential floor(s) on record for this project — treat as a
        starting point, not a filed rate.
      </p>
      <div class="mt-50 text-right">
        <b-link class="small mr-1" @click="lookupAppraisedRate()">Search again</b-link>
        <b-link class="small" @click="lookupStatus = 'idle'">Dismiss</b-link>
      </div>
    </b-alert>

    <b-alert v-if="lookupStatus === 'ambiguous'" variant="secondary" show class="p-1 mb-1">
      <p class="small mb-50">
        Project name matches <strong>{{ candidates.length }}</strong> registered projects. Pick the
        correct one, or edit the name above:
      </p>
      <div
        v-for="c in candidates"
        :key="c.condoId"
        class="d-flex justify-content-between align-items-center py-50 border-bottom"
      >
        <div>
          <div class="small font-weight-bold">{{ c.name }}</div>
          <div class="text-muted" style="font-size: 11px;">{{ c.amphur }}, {{ c.tumbon }}</div>
        </div>
        <b-button size="sm" variant="outline-primary" @click="selectCandidate(c)">Select</b-button>
      </div>
    </b-alert>

    <b-alert v-if="lookupStatus === 'empty'" variant="light" show class="p-1 mb-1">
      <p class="small mb-0">
        <strong>No Treasury Dept. records found</strong> for “{{ projectName }}”. Check that the name
        above is in Thai and spelled the way it's registered — the project may also be too new or
        unregistered under this name. Edit and search again, or enter the rate manually.
      </p>
    </b-alert>

    <div class="stat-box bg-light-primary rounded mt-1">
      <span class="text-body">
        <feather-icon icon="TrendingUpIcon" size="14" class="mr-50 text-primary" />Appraised Value (auto)
      </span>
      <strong class="text-primary">฿ {{ formatNumber(appraisedValue) }}</strong>
    </div>
  </div>
</template>

<script>
import axios from '@axios'
import {
  BRow, BCol, BFormGroup, BFormInput, BInputGroup, BButton, BAlert, BBadge, BSpinner, BLink,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatNumber, parseNumber } from '../format'
import UploadTitleDeed from '../UploadTitleDeed.vue'

export default {
  components: {
    BRow, BCol, BFormGroup, BFormInput, BInputGroup, BButton, BAlert, BBadge, BSpinner, BLink, UploadTitleDeed,
  },
  props: {
    propertyId: { type: [String, Number], required: true },
    projectName: { type: String, default: null },
    tower: { type: String, default: null },
    floor: { type: String, default: null },
    unitNo: { type: String, default: null },
    unitArea: { type: Number, default: null },
    appraisedRatePerSqm: { type: Number, default: null },
    treasuryCondoId: { type: [String, Number], default: null },
    appraisedValue: { type: Number, default: 0 },
  },
  data() {
    return {
      lookupStatus: 'idle', // idle | loading | matched | estimated | ambiguous | empty
      matchDetail: null,
      candidates: [],

      projectSuggestions: [],
      showSuggestions: false,
      suggestLoading: false,
      suggestTimer: null,
    }
  },
  computed: {
    unitNoModel: {
      get() { return this.unitNo },
      set(value) { this.$emit('update:unitNo', value) },
    },
  },
  beforeDestroy() {
    clearTimeout(this.suggestTimer)
  },
  methods: {
    formatNumber,
    parseNumber,
    resetStaleLookup(clearCondoId) {
      if (['matched', 'estimated', 'ambiguous', 'empty'].includes(this.lookupStatus)) {
        this.lookupStatus = 'idle'
        this.matchDetail = null
        this.candidates = []
      }
      if (clearCondoId) this.$emit('update:treasuryCondoId', null)
    },
    onProjectNameInput(value) {
      this.$emit('update:projectName', value)
      this.resetStaleLookup(true)

      clearTimeout(this.suggestTimer)
      const q = value ? value.trim() : ''

      if (q.length < 2) {
        this.projectSuggestions = []
        this.showSuggestions = false
        return
      }

      this.showSuggestions = true
      this.suggestTimer = setTimeout(() => this.fetchProjectSuggestions(q), 350)
    },
    onProjectNameFocus() {
      if (this.projectSuggestions.length) this.showSuggestions = true
    },
    onTowerOrFloorInput(field, value) {
      this.$emit(`update:${field}`, value)
      this.resetStaleLookup(false)
    },
    async fetchProjectSuggestions(q) {
      this.suggestLoading = true
      try {
        const { data } = await axios.get('property/v1/suggestproject', { params: { q } })
        this.projectSuggestions = data?.data || []
      } catch (err) {
        this.projectSuggestions = []
        console.error('Error fetching project suggestions:', err)
      } finally {
        this.suggestLoading = false
      }
    },
    selectSuggestion(candidate) {
      this.showSuggestions = false
      this.projectSuggestions = []
      this.selectCandidate(candidate)
    },
    async lookupAppraisedRate(condoId = null) {
      this.lookupStatus = 'loading'

      try {
        const { data } = await axios.get(`property/v1/appraisedrate/${this.propertyId}`, {
          params: {
            projectName: this.projectName,
            tower: this.tower,
            floor: this.floor,
            condoId: condoId || this.treasuryCondoId,
          },
        })
        const result = data?.data || {}

        if (result.status === 'matched' || result.status === 'estimated') {
          this.$emit('update:appraisedRatePerSqm', result.rate)
          this.matchDetail = result.matchDetail
          const newCondoId = result.matchDetail?.condoId || this.treasuryCondoId
          if (newCondoId !== this.treasuryCondoId) this.$emit('update:treasuryCondoId', newCondoId)
          this.lookupStatus = result.status
        } else if (result.status === 'ambiguous') {
          this.candidates = result.candidates || []
          this.lookupStatus = 'ambiguous'
        } else {
          this.lookupStatus = 'empty'
        }
      } catch (err) {
        this.lookupStatus = 'idle'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: err.response?.data?.message || 'Failed to look up appraised rate',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        console.error('Error looking up appraised rate:', err)
      }
    },
    selectCandidate(candidate) {
      this.$emit('update:projectName', candidate.name)
      this.$emit('update:treasuryCondoId', candidate.condoId)
      this.candidates = []
      this.lookupAppraisedRate(candidate.condoId)
    },
  },
}
</script>
